import React, { useEffect, useState } from 'react';
import {
    Tabs,
    Table,
    Button,
    Form,
    Input,
    Select,
    Modal,
    Tag,
    Space,
    Divider,
    Card,
    Typography,
    Upload,
    UploadFile,
    Spin,
    Flex,
    Popconfirm,
    message,
    TableProps
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EyeOutlined,
    CloseOutlined,
    CheckOutlined,
    UploadOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    AppstoreAddOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { useStore } from '../../stores/root-store-context';
import { TicketChat } from './TicketChat';
import { AuthorType, getEnumName, NewTicket, Ticket, TicketStatus } from '../../api/TicketApi';
import { observer } from 'mobx-react-lite';
import dayjs, { Dayjs } from 'dayjs';
import { TicketDetails } from './TicketDetails';
import Layout from '../../components/layout/Layout';
import { useTranslation } from 'react-i18next';
import { MessageOutlined, CommentOutlined, WechatOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import AssignKanbanTask from './AssignKanbanTask';
import { getUserComponent } from '../../helpers/user';
import { el } from 'date-fns/locale';

// ==================== TYPES ====================


interface CreateTicketFormValues {
    category: string;
    subject: string;
    description: string;
}

// ==================== COMPONENT ====================
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

type RouteParam = {
    [key: string]: string | undefined
}

const SupportSystem: React.FC = observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams<RouteParam>();
    const ticketPublicId = params.publicId;


    const [activeTab, setActiveTab] = useState<string>('active');
    const { ticketStore, ticketChatStore, applicationStore, employeesStore, projectsStore } = useStore();
    const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
    const [viewModalVisible, setViewModalVisible] = useState<boolean>(false);
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
    const [form] = Form.useForm<CreateTicketFormValues>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [assignModalVisible, setAssignModalVisible] = useState<boolean>(false);


    useEffect(() => {
        employeesStore.getUsers();
        projectsStore.getAll();
        ticketStore.getAllActiveTickets().then((tickets) => {
            if (ticketPublicId) {
                ticketStore.getTicketsById(ticketPublicId).then((ticket) => {
                    if (ticket) {
                        setSelectedTicket(ticket);
                        setViewModalVisible(true);
                    }
                });
            }
        });
    }, []);

    useEffect(() => {
        let element = ticketStore.activeTickets.find(ticket => ticket.publicId == ticketPublicId || ticket.id == ticketPublicId);
        element = element || ticketStore.closedTickets.find(ticket => ticket.publicId == ticketPublicId || ticket.id == ticketPublicId);

        if (element) {
            ticketStore.setCurrentTicket(element);
            setViewModalVisible(true);
        } else {
            ticketStore.getAllActiveTickets().then((tickets) => {
                const ticket = tickets?.find(ticket => ticket.publicId == ticketPublicId || ticket.id == ticketPublicId);
                if (ticket) {
                    ticketStore.setCurrentTicket(ticket);
                    setViewModalVisible(true);
                }
            });
        }

    }, [ticketPublicId]);

    const setByTab = () => {
        const newFiltered: Ticket[] = ticketStore.activeTickets.filter(ticket => {
            if (activeTab === 'my') {
                return ticket.email === applicationStore.user.email;
            }
            else{
                return true;
            }
        }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setFilteredTickets(newFiltered);

    }

    const update = async () => {
        if (activeTab === 'closed') {
            await ticketStore.getAllCloseTickets().then((tickets) => {
                if (tickets) {
                    setFilteredTickets(tickets);
                }
            });
        } else if (activeTab === 'active') {
            await ticketStore.getAllActiveTickets().then((tickets) => {
                if (tickets) {
                    setFilteredTickets(tickets);
                }
            });
        } else {
            setByTab()
        }
        setIsLoading(false);
    }

    useEffect(() => {
        update();
    }, [activeTab, applicationStore.user.email]);

    useEffect(() => {
        setByTab();
    }, [ticketStore.activeTickets]);


    const columns: any = [
        {
            title: t('TicketID'),
            dataIndex: 'publicId',
            key: 'publicId',
            sorter: (a: Ticket, b: Ticket) => a.publicId.localeCompare(b.publicId),
        },
        // {
        //     title: t('Subject'),
        //     dataIndex: 'subject',
        //     key: 'subject',
        //     width: '300px',
        //     sorter: (a: Ticket, b: Ticket) => a.subject.localeCompare(b.subject),
        // },
        {
            title: `${t('Subject')}/${t('Project')}/${t('Company')}/${t('User')}`,
            dataIndex: 'companyName',
            key: 'companyName',
            width: '100px',
            sorter: (a: Ticket, b: Ticket) => a.companyName?.localeCompare(b.companyName || ''),
            render: (text: string, record: Ticket) => <Flex vertical gap={10}>
                <div style={{ textAlign: 'center', fontSize: '16px' }}>
                    {record.subject}
                </div>
                <div style={{ textAlign: 'center' }}>
                    <Tag color='blue'>{record.companyName}</Tag>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <Tag color='green'>{record.companySystemName}</Tag>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <Tag color={record.userRole === 'Unregistered User' ? 'red' : 'blue'}>{record.userRole}</Tag>
                </div>
            </Flex>,
        },
        {
            title: `${t('Category')}/${t('Status')}/${t('Language')}`,
            dataIndex: 'category',
            key: 'category',
            render: (status: string, record: Ticket) => {
                let color = '';
                let icon = <ExclamationCircleOutlined />;
                switch (record.status) {
                    case 0:
                        color = 'error';
                        icon = <ExclamationCircleOutlined />;
                        break;
                    case 1:
                        color = 'blue';
                        icon = <ClockCircleOutlined />;
                        break;
                    case 2:
                        color = 'warning';
                        icon = <ExclamationCircleOutlined />;
                        break;
                    case 3:
                        color = 'red';
                        icon = <CheckCircleOutlined />;
                        break;
                    case 4:
                        color = 'red';
                        icon = <CheckCircleOutlined />;
                        break;
                }
                return <Flex vertical gap={10} style={{ textAlign: 'center' }}>
                    <div>
                        <Tag color="" style={{ fontSize: '14px' }}>{t(`supportCategories.${record.category}`) == `supportCategories.${record.category}` ? record.category : t(`supportCategories.${record.category}`)}</Tag>
                    </div>
                    <div>
                        <Tag icon={icon} color={color} style={{ fontSize: '16px' }}>
                            {t(getEnumName(TicketStatus, record.status))}
                        </Tag>
                    </div>
                    <div>
                        <Tag icon={<HomeOutlined />} style={{ fontSize: '16px', textTransform: 'uppercase' }}>
                            <span style={{ fontSize: '16px', textTransform: 'uppercase' }}>{record.lang || "en"}</span>
                        </Tag>
                    </div>
                </Flex>;
            },
            filters: [
                ...filteredTickets.map((ticket) => ({
                    text: t('supportCategories.' + ticket.category),
                    value: ticket.category,
                })).filter(
                    (ticket, index, self) =>
                        index === self.findIndex((t) => t.value === ticket.value)
                )
            ],
            onFilter: (value: string, record: Ticket) => record.category === value,
        },
        {
            title: t('CreatedAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a: Ticket, b: Ticket) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            render: (text: string, record: Ticket) => <Flex vertical gap={10}>
                <div>{record.userName}</div>
                <div>{record.email}</div>
                {record.createdAt && <div>{t("CreatedAt")}: <Tag color='blue' >{dayjs(record.createdAt).format('DD MMMM YYYY HH:mm')}</Tag></div>}
                {record.updatedAt && <div >{t("UpdatedAt")}: <Tag color='green'>{dayjs(record.updatedAt).format('DD MMMM YYYY HH:mm')}</Tag></div>}
                {record.closedAt && <div >{t("ClosedAt")}: <Tag color='green'>{dayjs(record.closedAt).format('DD MMMM YYYY HH:mm')}</Tag></div>}
            </Flex>,
        },
        {
            title: t('Kanban'),
            dataIndex: 'kanban',
            key: 'kanban',
            width: '300px',
            render: (text: string, record: Ticket) => {
                if (!record.kanbanCompanyId) {
                    return (
                        <Flex gap={10} vertical>
                            <Button
                                style={{ fontSize: '14px', padding: '4px 25px', height: '44px' }}
                                icon={<AppstoreAddOutlined style={{ fontSize: '24px' }} />}
                                onClick={() => {
                                    setSelectedTicket(record);
                                    setAssignModalVisible(true);
                                }}
                            >
                                {t('Create Task for Ticket')}
                            </Button>
                        </Flex>
                    )
                } else if (record.kanbanCompanyId && record.kanbanCompanyId.toString() != applicationStore.company.Id) {
                    return (
                        <Flex>
                            <div>{t('The ticket was taken by another support company.')}</div>
                        </Flex>
                    )
                } else {
                    return (
                        <Flex gap={10} vertical>
                            <div><a
                                style={{ textDecoration: 'underline' }}
                                href={`/${applicationStore.company?.Tenant}/projects/${record.kanbanProjectId}/tasks/${record.kanbanTicketPublicId}`}
                                target='_blank'
                                rel='noreferrer'>
                                {t('TaskDetails')} #{record.kanbanTicketPublicId}</a></div>
                            {record.kanbanProjectId && <div>{projectsStore.projects?.find(project => project.id == record.kanbanProjectId.toString())?.name}</div>}
                            {record.kanbanResposibleUserId && <Tag style={{ padding: 5 }} color='blue'>
                                <div style={{ marginBottom: 10 }}>{t("Assigned")}</div>
                                {getUserComponent(applicationStore.siteConfig.baseCloudinary, employeesStore.users?.find(user => user.id == record.kanbanResposibleUserId.toString()), applicationStore.company?.Tenant, 40)}
                            </Tag>}
                        </Flex>
                    )
                }
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '250px',
            render: (_: any, record: Ticket) => (
                <Space size="middle">
                    <Flex vertical>
                        <div style={{ width: '150px' }}>
                            📨 {t('Messages')}: <strong>{record.messages.filter(m => m.authorType === AuthorType.User).length}</strong>
                        </div>
                        <div style={{ width: '150px' }}>
                            📨 {t('Replies')}: <strong>{record.messages.filter(m => m.authorType === AuthorType.Support).length}</strong>
                        </div>
                    </Flex>
                    <Flex vertical gap={10}>

                        <Button
                            style={{ fontSize: '14px', padding: '4px 25px', height: '44px' }}
                            icon={<CommentOutlined style={{ fontSize: '30px' }} />}
                            onClick={() => handleViewTicket(record)}
                        >
                            {t('OpenChat')}
                        </Button>
                        {record.status !== TicketStatus.ResolvedCustomer && record.status !== TicketStatus.ResolvedSupport && (
                            <Popconfirm
                                title={t('ResolveTicket')}
                                description={t('ResolveTicketConfirmation')}
                                onConfirm={() => handleCloseTicket(record.id)}
                                okText={t('Yes')}
                                cancelText={t('No')}
                            >
                                <Button
                                    style={{ fontSize: '14px', padding: '4px 25px', height: '44px' }}
                                    icon={<CloseOutlined style={{ fontSize: '24px' }} />}
                                    danger
                                >
                                    {t('CloseTicket')}
                                </Button>
                            </Popconfirm>
                        )}

                    </Flex>
                </Space>
            ),
        },
    ];

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleViewTicket = (ticket: Ticket) => {
        setSelectedTicket(ticket);
        setViewModalVisible(true);
        navigate(`/support-system/${ticket.publicId}`);
    };

    const handleCloseTicket = (ticketId: string) => {
        ticketStore.update(ticketId, { status: TicketStatus.ResolvedSupport } as Ticket).then(() => {
            message.success('Ticket closed successfully');
            ticketStore.activeTickets = ticketStore.activeTickets.filter(ticket => ticket.id !== ticketId);
            // ticketStore.getAllActiveTickets().then((tickets) => {
            //     if (tickets) {
            //         update();
            //     }
            // });
        });
    };

    const [statusFilter, setStatusFilter] = useState<number[]>([]);
    const [projectFilter, setProjectFilter] = useState<string[]>([]);
    const handleStatusClick = (status: number | null) => {
        if (status == null) {
            setStatusFilter([]);
            return;
        }
        if (statusFilter.includes(status)) {
            setStatusFilter(statusFilter.filter(p => p !== status));
            return;
        } else {
            setStatusFilter([...statusFilter, status]);
        }
    };

    const handleProjectClick = (project: string | null) => {
        if (project == null) {
            setProjectFilter([]);
            return;
        }
        if (projectFilter.includes(project)) {
            setProjectFilter(projectFilter.filter(p => p !== project));
            return;
        } else {
            setProjectFilter([...projectFilter, project]);
        }
    };

    return (
        <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={t("Support System")}>
            <div style={{ padding: '24px' }}>
                <Card>
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                    >
                        <TabPane tab={t("activeTickets")} key="active" children={
                            <Flex align='center' style={{ padding: '10px 0' }} gap={10}>
                                <h5>{t('Status')}: </h5>
                                <Button type={statusFilter.length === 0 ? 'primary' : 'default'} onClick={() => handleStatusClick(null)}>{t("All")}</Button>
                                <Button type={statusFilter.includes(0) ? 'primary' : 'default'} onClick={() => handleStatusClick(0)}>{t("New")}</Button>
                                <Button type={statusFilter.includes(1) ? 'primary' : 'default'} onClick={() => handleStatusClick(1)}>{t("Replied")}</Button>
                                <Button type={statusFilter.includes(2) ? 'primary' : 'default'} onClick={() => handleStatusClick(2)}>{t("NewMessage")}</Button>
                            </Flex>
                        } />
                        <TabPane tab={t("ViewClosedTickets")} key="closed" children={
                            <Flex align='center' style={{ padding: '10px 0' }} gap={10}>
                                <h5>{t('Status')}: </h5>
                                <Button type={statusFilter.length === 0 ? 'primary' : 'default'} onClick={() => handleStatusClick(null)}>{t("All")}</Button>
                                <Button type={statusFilter.includes(3) ? 'primary' : 'default'} onClick={() => handleStatusClick(3)}>{t("Resolved")}</Button>
                                <Button type={statusFilter.includes(4) ? 'primary' : 'default'} onClick={() => handleStatusClick(4)}>{t("ResolvedSupport")}</Button>
                            </Flex>
                        } />
                    </Tabs>

                    {/* <Spin spinning={ticketStore.isLoading || isLoading}> */}

                    <Flex align='center' style={{ padding: '10px 0' }} gap={10}>
                        <h5>{t('Project')}: </h5>
                        <Button type={projectFilter.length === 0 ? 'primary' : 'default'} onClick={() => handleProjectClick(null)}>{t("All")}</Button>
                        {
                            filteredTickets.map((ticket) => ({
                                text: ticket.companyName,
                                value: ticket.companyName,
                            })).filter(
                                (ticket, index, self) =>
                                    index === self.findIndex((t) => t.value === ticket.value)
                            ).map(el => {
                                return (
                                    <Button type={projectFilter.includes(el.value) ? 'primary' : 'default'} onClick={() => handleProjectClick(el.value)}>{el.text}</Button>
                                )
                            })
                        }

                    </Flex>
                    <Table
                        columns={columns}
                        dataSource={filteredTickets.filter(ticket => {
                            if (statusFilter.length > 0) {
                                return statusFilter.includes(ticket.status);
                            }
                            return true;
                        }).filter(ticket => {
                            if (projectFilter.length > 0) {
                                return projectFilter.includes(ticket.companyName);
                            }
                            return true;
                        })}
                        loading={ticketStore.isLoading || isLoading}
                        rowKey="id"
                        bordered
                        pagination={{ pageSize: 50 }}
                        scroll={{ x: 'max-content' }}

                    />
                    {/* </Spin> */}
                </Card>

                {/* Create Ticket Modal */}

                <TicketDetails
                    mode="support"
                    visible={viewModalVisible}
                    ticket={selectedTicket}
                    onClose={() => {
                        setViewModalVisible(false);
                        ticketChatStore.disconnect(selectedTicket?.id || '');
                        navigate(`/support-system`);
                    }}
                    onCloseTicket={() => {

                    }}
                />
                {selectedTicket && (
                    <AssignKanbanTask ticket={selectedTicket} visible={assignModalVisible} setVisible={(visible: boolean) => {
                        setAssignModalVisible(visible)
                        setSelectedTicket(null);
                    }} />
                )}
            </div>
        </Layout>

    );
});

export default SupportSystem;